import React from 'react';
import { User } from '../types';
import { CheckCircle2, AlertCircle, Sparkles, Gem, LogOut } from 'lucide-react';
import SparkleDust from './SparkleDust';

interface ProfileProps {
  user: User;
  storyCount: number;
  onLogout: () => void;
  onGoToShop: () => void;
  onBack: () => void;
}

const Profile: React.FC<ProfileProps> = ({ user, storyCount, onLogout, onGoToShop, onBack }) => {
  const joinDate = new Date(user.joinedAt || Date.now()).toLocaleDateString('sq-AL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="animate-in slide-in-from-bottom-8 duration-500 max-w-xl mx-auto w-full px-4">
      <div className="glass-card rounded-[2.5rem] overflow-hidden border border-white/10 shadow-2xl relative">
        <div className="absolute top-0 right-0 p-6">
            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 ${user.isVerified ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                {user.isVerified ? (
                  <>
                    <span className="text-sm">✅</span> Verifikuar
                  </>
                ) : (
                  <>
                    <span className="text-sm">⚠️</span> Pa Verifikuar
                  </>
                )}
            </span>
        </div>

        {/* Profile Header */}
        <div className="bg-gradient-to-br from-indigo-600/40 to-purple-600/40 p-10 text-center border-b border-white/10">
          <div className="relative inline-block mb-4">
            <div className="w-28 h-28 bg-gradient-to-tr from-yellow-400 to-orange-500 rounded-3xl mx-auto flex items-center justify-center text-5xl shadow-2xl border-4 border-white/20 rotate-3 group-hover:rotate-0 transition-transform text-white font-black">
                {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="absolute -bottom-2 -right-2 bg-indigo-600 w-10 h-10 rounded-full border-4 border-[#1e1b4b] flex items-center justify-center">
              <span className="text-sm">💎</span>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-white font-fairytale tracking-wide">{user.name}</h2>
          <p className="text-indigo-100/80 text-sm font-medium mt-1">{user.email}</p>
        </div>

        {/* Info Bars */}
        <div className="p-4 grid grid-cols-2 gap-4 bg-black/20">
            <div className="text-center p-4">
                <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-1">Anëtar që nga</p>
                <p className="text-white font-bold text-xs">{joinDate}</p>
            </div>
            <div className="text-center p-4">
                <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-1">ID e Llogarisë</p>
                <p className="text-white/60 font-mono text-[10px] truncate max-w-[100px] mx-auto">#{user.id.slice(0, 8)}</p>
            </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 border-y border-white/10">
          <div className="p-8 text-center border-r border-white/10 hover:bg-white/10 transition-colors">
            <p className="text-[10px] font-black text-indigo-200 uppercase tracking-[0.2em] mb-2">Libri i Përrallave</p>
            <p className="text-4xl font-bold text-white font-comic">{storyCount}</p>
            <p className="text-[10px] text-indigo-100/60 mt-1 italic">histori të krijuara</p>
          </div>
          <div className="p-8 text-center hover:bg-white/10 transition-colors">
            <p className="text-[10px] font-black text-yellow-400 uppercase tracking-[0.2em] mb-2">Thesari Magjik</p>
            <p className="text-4xl font-bold text-yellow-100 font-comic flex items-center justify-center gap-2">
              {user.tokens} <span className="text-3xl">💎</span>
            </p>
            <p className="text-[10px] text-yellow-400/60 mt-1 italic">xhevahire të mbetura</p>
          </div>
        </div>

        {/* Actions */}
        <div className="p-8 space-y-4">
          <button 
            onClick={onGoToShop}
            className="w-full sparkle-nav-btn active py-5 rounded-2xl bg-yellow-400/10 border-yellow-400/20 text-yellow-100 font-bold text-sm transition-all shadow-lg"
          >
            <SparkleDust />
            <span className="text-xl">💎</span>
            Shto Xhevahire Magjike
          </button>

          <button 
            onClick={onLogout}
            className="w-full sparkle-nav-btn py-5 rounded-2xl bg-red-500/10 border-red-500/20 text-red-400 font-bold text-sm transition-all shadow-inner"
          >
            <SparkleDust />
            <span className="text-xl">🚪</span>
            Mbyll Sesionin Magjik
          </button>
        </div>
      </div>

      <button 
        onClick={onBack}
        className="mt-10 block mx-auto text-indigo-300/40 hover:text-white font-black uppercase text-[10px] tracking-[0.3em] transition-all"
      >
        ← Kthehu te magjia
      </button>
    </div>
  );
};

export default Profile;